import { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import type { Club, PaginatedResult } from "@/types";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";
import { ExportExcelButton } from "@/components/ui/ExportExcelButton";
import { SearchSelect } from "@/components/ui/SearchSelect";
import { SortHeader, nextSort, type SortState } from "@/components/ui/SortHeader";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { AuditoriaDetalleModal, type RegistroAuditoriaRow } from "@/components/AuditoriaDetalleModal";
import { describeAccion } from "@/lib/describeAccion";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const fechaFormatter = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
const horaFormatter = new Intl.DateTimeFormat("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

interface UsuarioOption {
  id: number;
  nombre: string;
  apellidos: string | null;
  email: string;
}

const nombreCompleto = (u: { nombre: string; apellidos: string | null }) => `${u.nombre} ${u.apellidos ?? ""}`.trim();

// Fecha que elige el usuario → instante ISO calculado EN EL NAVEGADOR (así el día se interpreta en
// su zona horaria, no en la del servidor): "desde" es el principio del día y "hasta" el final.
function limiteIso(fecha: string, esFin: boolean): string | null {
  if (!fecha) return null;
  const d = new Date(`${fecha}T${esFin ? "23:59" : "00:00"}:00`);
  if (Number.isNaN(d.getTime())) return null;
  if (esFin) d.setSeconds(59, 999);
  return d.toISOString();
}

type SortKey = "fecha" | "hora" | "nombre" | "apellidos";

export function AuditoriaPage() {
  const isSuperAdmin = useAuthStore((s) => s.user?.role === "SUPER_ADMIN");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [accionInput, setAccionInput] = useState("");
  const accion = useDebouncedValue(accionInput.trim(), 400);
  const [usuario, setUsuario] = useState<UsuarioOption | null>(null);
  const [club, setClub] = useState<Club | null>(null);
  const [desdeFecha, setDesdeFecha] = useState("");
  const [hastaFecha, setHastaFecha] = useState("");
  // Por defecto, lo más reciente primero. Pulsar una cabecera ordena por esa columna (en el servidor).
  const [sort, setSort] = useState<SortState<SortKey>>({ key: "fecha", dir: "desc" });
  const [detalle, setDetalle] = useState<RegistroAuditoriaRow | null>(null);

  const hasActiveFilters = !!(accionInput || usuario || club || desdeFecha || hastaFecha);

  function reset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  function clearFilters() {
    setAccionInput("");
    setUsuario(null);
    setClub(null);
    setDesdeFecha("");
    setHastaFecha("");
    setPage(1);
  }

  // Mismos filtros para el listado y para la exportación — el listado añade "page" encima.
  const filterParams = useMemo(() => {
    const p = new URLSearchParams();
    if (accion) p.set("accion", accion);
    if (usuario) p.set("usuarioId", String(usuario.id));
    if (isSuperAdmin && club) p.set("clubId", String(club.id));
    const desde = limiteIso(desdeFecha, false);
    if (desde) p.set("desde", desde);
    const hasta = limiteIso(hastaFecha, true);
    if (hasta) p.set("hasta", hasta);
    p.set("sort", sort.key);
    p.set("dir", sort.dir);
    // La hora se ordena según la zona horaria del navegador (la que ve el usuario).
    p.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone);
    return p;
  }, [accion, usuario, club, desdeFecha, hastaFecha, sort, isSuperAdmin]);

  function toggleSort(key: SortKey) {
    setSort((s) => nextSort(s, key));
    setPage(1);
  }

  const listQueryString = useMemo(() => {
    const p = new URLSearchParams(filterParams);
    p.set("page", String(page));
    p.set("limit", String(limit));
    return p.toString();
  }, [filterParams, page, limit]);

  const { data: resp, isLoading } = useQuery({
    queryKey: ["auditoria", listQueryString],
    queryFn: async () => (await api.get<PaginatedResult<RegistroAuditoriaRow>>(`/auditoria?${listQueryString}`)).data,
    placeholderData: keepPreviousData,
  });

  const registros = resp?.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs items={[{ label: "Logs" }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
            <ClipboardList className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Logs</h1>
            <p className="text-ink-400">Quién ha hecho cada acción en el panel y cuándo.</p>
          </div>
        </div>
        <ExportExcelButton url={`/auditoria/export.xlsx?${filterParams.toString()}`} filename="auditoria.xlsx" />
      </div>

      <Card>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <div className="col-span-2 sm:col-span-1">
              <Input
                label="Acción"
                placeholder="p.ej. crear, login…"
                value={accionInput}
                onChange={(e) => reset(setAccionInput)(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <SearchSelect<UsuarioOption>
                label="Usuario"
                value={usuario}
                onChange={reset(setUsuario)}
                queryKey={["auditoria", "usuarios"]}
                fetchOptions={async (search) => (await api.get<UsuarioOption[]>("/auditoria/usuarios", { params: { search } })).data}
                getKey={(u) => u.id}
                getLabel={nombreCompleto}
                getHint={(u) => u.email}
                placeholder="Todos"
                searchPlaceholder="Nombre o email…"
                clearable
              />
            </div>
            {isSuperAdmin && (
              <div className="col-span-2 sm:col-span-1">
                <SearchSelect<Club>
                  label="Club"
                  value={club}
                  onChange={reset(setClub)}
                  queryKey={["auditoria", "clubs"]}
                  fetchOptions={async (search) => (await api.get<{ data: Club[] }>("/clubs", { params: { nombre: search, limit: 25 } })).data.data}
                  getKey={(c) => c.id}
                  getLabel={(c) => c.nombre}
                  placeholder="Todos"
                  searchPlaceholder="Nombre del club…"
                  clearable
                />
              </div>
            )}
            <Input type="date" label="Desde" value={desdeFecha} onChange={(e) => reset(setDesdeFecha)(e.target.value)} className="w-full" />
            <Input type="date" label="Hasta" value={hastaFecha} onChange={(e) => reset(setHastaFecha)(e.target.value)} className="w-full" />
          </div>
          {hasActiveFilters && (
            <div>
              <Button onClick={clearFilters}>Limpiar filtros</Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <TableSkeleton rows={4} cols={5} />
        ) : !registros.length ? (
          <p className="p-6 text-sm text-ink-400">
            {hasActiveFilters ? "Ningún registro coincide con los filtros." : "Todavía no hay ninguna acción registrada."}
          </p>
        ) : (
          <table className="w-full table-fixed text-left text-sm">
            <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
              <tr>
                <th className="px-4 py-3 sm:w-32">
                  <span className="sm:hidden">
                    <SortHeader label="Registro" active={sort.key === "fecha"} dir={sort.dir} onClick={() => toggleSort("fecha")} />
                  </span>
                  <span className="hidden sm:inline">
                    <SortHeader label="Fecha" active={sort.key === "fecha"} dir={sort.dir} onClick={() => toggleSort("fecha")} />
                  </span>
                </th>
                <th className="hidden px-4 py-3 sm:table-cell sm:w-28">
                  <SortHeader label="Hora" active={sort.key === "hora"} dir={sort.dir} onClick={() => toggleSort("hora")} />
                </th>
                <th className="hidden px-4 py-3 md:table-cell md:w-[16%]">
                  <SortHeader label="Nombre" active={sort.key === "nombre"} dir={sort.dir} onClick={() => toggleSort("nombre")} />
                </th>
                <th className="hidden px-4 py-3 lg:table-cell lg:w-[16%]">
                  <SortHeader label="Apellidos" active={sort.key === "apellidos"} dir={sort.dir} onClick={() => toggleSort("apellidos")} />
                </th>
                <th className="hidden px-4 py-3 font-semibold sm:table-cell">Acción</th>
                <th className="hidden px-4 py-3 sm:table-cell sm:w-36" />
              </tr>
            </thead>
            <tbody>
              {registros.map((r) => {
                const fecha = new Date(r.createdAt);
                const nombreUsuario = r.usuario ? nombreCompleto(r.usuario) : "Sistema";
                return (
                  <tr
                    key={r.id}
                    onClick={() => setDetalle(r)}
                    className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50/50"
                  >
                    <td className="px-4 py-3 text-ink-500">
                      {/* Móvil: una sola "tarjeta" con todo; desde sm, solo la fecha (el resto tiene columna) */}
                      <div className="flex flex-col gap-2 sm:hidden">
                        <div className="min-w-0">
                          <p className="font-medium break-words text-ink-800">{describeAccion(r.accion)}</p>
                          <p className="text-xs break-words text-ink-400">
                            {nombreUsuario} · {fechaFormatter.format(fecha)} · {horaFormatter.format(fecha)}
                          </p>
                        </div>
                        <div>
                          <Button onClick={(e) => { e.stopPropagation(); setDetalle(r); }}>Ver detalle</Button>
                        </div>
                      </div>
                      <span className="hidden whitespace-nowrap sm:inline">{fechaFormatter.format(fecha)}</span>
                    </td>
                    <td className="hidden px-4 py-3 whitespace-nowrap text-ink-500 sm:table-cell">{horaFormatter.format(fecha)}</td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      <p className="truncate font-medium text-ink-800" title={r.usuario?.nombre ?? "Sistema"}>
                        {r.usuario?.nombre ?? "Sistema"}
                      </p>
                      <p className="truncate text-xs text-ink-400 lg:hidden">{r.usuario?.apellidos ?? ""}</p>
                    </td>
                    <td className="hidden truncate px-4 py-3 text-ink-600 lg:table-cell" title={r.usuario?.apellidos ?? undefined}>
                      {r.usuario?.apellidos ?? "—"}
                    </td>
                    <td className="hidden px-4 py-3 sm:table-cell">
                      <p className="truncate text-ink-700" title={describeAccion(r.accion)}>
                        {describeAccion(r.accion)}
                      </p>
                      <p className="truncate text-xs text-ink-400 md:hidden">{nombreUsuario}</p>
                    </td>
                    <td className="hidden px-4 py-3 text-right sm:table-cell">
                      <Button onClick={(e) => { e.stopPropagation(); setDetalle(r); }}>Ver detalle</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {resp && (
        <Pagination
          page={resp.page}
          totalPages={resp.totalPages}
          total={resp.total}
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      )}

      {detalle && <AuditoriaDetalleModal registro={detalle} onOpenChange={(open) => !open && setDetalle(null)} />}
    </div>
  );
}
